import { create } from 'zustand';
import type { FlowSummary } from '@/lib/api/email-flows/types';
import type { Tag } from '@/features/email-flows/types/audience.types';
import type { BroadcastFormData } from '../types/broadcast.types';
import { TemplateVariables } from '../../emails/types';
import type { GeneratedDraft } from '../types/generation.types';

interface BroadcastEditorStore {
    flowData: FlowSummary | null;

    // Template data
    templateVariables: TemplateVariables | null;
    templateDefaults: BroadcastFormData | null;
    defaultValues: BroadcastFormData | null;

    // Audience selection
    selectedTags: Tag[];
    selectedExcludedTags: Tag[];
    recipientCount: number;

    // Metadata (computed from broadcast data)
    broadcastStatus: string;

    // Generated draft from Stanley
    generatedDraft: GeneratedDraft | null;

    // Actions
    setFlowData: (flowData: FlowSummary | null) => void;
    setTemplateData: (
        variables: TemplateVariables,
        defaults: BroadcastFormData,
        defaultValues: BroadcastFormData
    ) => void;
    setSelectedTags: (tags: Tag[]) => void;
    setSelectedExcludedTags: (tags: Tag[]) => void;
    setRecipientCount: (count: number) => void;
    setMetadata: (metadata: { broadcastStatus: string }) => void;
    setGeneratedDraft: (draft: GeneratedDraft | null) => void;
    reset: () => void;
}

const initialState = {
    flowData: null,
    templateVariables: null,
    templateDefaults: null,
    defaultValues: null,
    selectedTags: [],
    selectedExcludedTags: [],
    recipientCount: 0,
    broadcastStatus: '',
    generatedDraft: null,
};

export const useBroadcastEditorStore = create<BroadcastEditorStore>(set => ({
    ...initialState,

    setFlowData: (flowData: FlowSummary | null) => set({ flowData }),

    setTemplateData: (variables, defaults, defaultValues) =>
        set({
            templateVariables: variables,
            templateDefaults: defaults,
            defaultValues,
        }),

    setSelectedTags: (tags: Tag[]) => set({ selectedTags: tags }),

    setSelectedExcludedTags: (tags: Tag[]) => set({ selectedExcludedTags: tags }),

    setRecipientCount: (count: number) => set({ recipientCount: count }),

    setMetadata: metadata => set(metadata),

    setGeneratedDraft: (draft: GeneratedDraft | null) => set({ generatedDraft: draft }),

    reset: () => set(initialState),
}));
